<template>
  <div class="rf-root" @mouseenter="pauseTimer" @mouseleave="resumeTimer">
    <div class="rf-grid">

      <!-- LEFT: Text column -->
      <div class="rf-left">
        <h2 class="rf-heading">What else can Raycast do?</h2>
        <p class="rf-text">
          <span
            v-for="(feat, i) in features"
            :key="i"
            class="rf-phrase"
            :class="{ 'rf-phrase--lit': activeIndex === i || hoveredIndex === i }"
            @mouseenter="hoveredIndex = i"
            @mouseleave="hoveredIndex = null"
            @click="jumpTo(i)"
          >{{ feat.phrase }}</span>
          <span class="rf-phrase rf-phrase--static">And much, much more.</span>
        </p>
      </div>

      <!-- RIGHT: Visual column -->
      <div class="rf-right">
        <!-- Mockup area -->
        <div class="rf-mockup-wrap">
          <div class="rf-mockup-bg"></div>

          <transition name="rf-fade" mode="out-in">
            <div class="rf-window" :key="activeIndex">
              <!-- Window header -->
              <div class="rf-win-header">
                <button class="rf-win-back">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M9 11L5 7l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div class="rf-win-search">
                  <span class="rf-win-cursor"></span>
                  {{ features[activeIndex].searchText }}
                </div>
              </div>
              <div class="rf-win-divider"></div>

              <!-- Window body -->
              <div class="rf-win-body">

                <!-- NOTES -->
                <template v-if="activeIndex === 0">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Good Afternoon!</p>
                      <p class="rf-mock-sub">You have <strong>3 notes</strong> pinned.</p>
                    </div>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-section-label">Recientes <span>Oct 26</span></div>
                  <div class="rf-list-item rf-list-item--active">
                    <span class="rf-item-dot rf-item-dot--yellow"></span>
                    <span class="rf-item-text">Meeting Notes — Standup</span>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">Shopping List</span>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">Ideas for Q4</span>
                  </div>
                  <div class="rf-section-label" style="margin-top:8px">Esta semana</div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">Personal Goals 2025</span>
                  </div>
                </template>

                <!-- FLIGHTS -->
                <template v-else-if="activeIndex === 1">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Flight Tracker</p>
                      <p class="rf-mock-sub">Live status for <strong>2 flights</strong> today.</p>
                    </div>
                    <span class="rf-badge rf-badge--green">On time</span>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-flight-card rf-flight-card--active">
                    <div class="rf-flight-row">
                      <div class="rf-flight-city">
                        <span class="rf-flight-code">BOG</span>
                        <span class="rf-flight-time">06:40</span>
                      </div>
                      <div class="rf-flight-line">
                        <span class="rf-flight-duration">5h 20m</span>
                        <div class="rf-flight-track">
                          <div class="rf-flight-plane">✈</div>
                        </div>
                      </div>
                      <div class="rf-flight-city rf-flight-city--right">
                        <span class="rf-flight-code">MIA</span>
                        <span class="rf-flight-time">12:00</span>
                      </div>
                    </div>
                    <div class="rf-flight-meta">
                      <span>Avianca · AV204</span>
                      <span class="rf-badge rf-badge--green">On time</span>
                    </div>
                  </div>
                  <div class="rf-section-label" style="margin-top:10px">Siguiente</div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">MIA → JFK · 14:30</span>
                  </div>
                </template>

                <!-- SEARCH FILES -->
                <template v-else-if="activeIndex === 2">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Search Files</p>
                      <p class="rf-mock-sub"><strong>2,847</strong> files indexed.</p>
                    </div>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-section-label">Resultados para "design"</div>
                  <div class="rf-list-item rf-list-item--active">
                    <span class="rf-file-icon rf-file-icon--fig">Fig</span>
                    <div class="rf-file-info">
                      <span class="rf-item-text">design-system-v3.fig</span>
                      <span class="rf-file-path">~/Documents/Design/</span>
                    </div>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-file-icon rf-file-icon--pdf">PDF</span>
                    <div class="rf-file-info">
                      <span class="rf-item-text">brand-guidelines.pdf</span>
                      <span class="rf-file-path">~/Downloads/</span>
                    </div>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-file-icon rf-file-icon--png">PNG</span>
                    <div class="rf-file-info">
                      <span class="rf-item-text">design-tokens-export.png</span>
                      <span class="rf-file-path">~/Desktop/</span>
                    </div>
                  </div>
                </template>

                <!-- MANAGE WINDOWS -->
                <template v-else-if="activeIndex === 3">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Window Management</p>
                      <p class="rf-mock-sub"><strong>6 windows</strong> open right now.</p>
                    </div>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-win-grid">
                    <div class="rf-win-cell rf-win-cell--active">
                      <div class="rf-win-app">VS Code</div>
                    </div>
                    <div class="rf-win-cell">
                      <div class="rf-win-app">Chrome</div>
                    </div>
                    <div class="rf-win-cell">
                      <div class="rf-win-app">Figma</div>
                    </div>
                    <div class="rf-win-cell">
                      <div class="rf-win-app">Slack</div>
                    </div>
                    <div class="rf-win-cell">
                      <div class="rf-win-app">Terminal</div>
                    </div>
                    <div class="rf-win-cell">
                      <div class="rf-win-app">Notion</div>
                    </div>
                  </div>
                  <div class="rf-win-commands">
                    <div class="rf-cmd-chip">Left Half</div>
                    <div class="rf-cmd-chip rf-cmd-chip--active">Maximize</div>
                    <div class="rf-cmd-chip">Right Half</div>
                  </div>
                </template>

                <!-- PLAN YOUR DAY (like the screenshot) -->
                <template v-else-if="activeIndex === 4">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Good Morning!</p>
                      <p class="rf-mock-sub">You have <strong>2 events</strong> today.</p>
                    </div>
                    <span class="rf-next-up">Next up:<br><strong>Sprint Ki…</strong></span>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-section-label">Today <span>Oct 26</span></div>
                  <div class="rf-list-item rf-list-item--active">
                    <span class="rf-item-dot rf-item-dot--yellow"></span>
                    <span class="rf-item-time">9:00 am – 9:30 am</span>
                    <span class="rf-item-text">Sprint Kickoff</span>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-time">12:00 pm – 6:00 pm</span>
                    <span class="rf-item-text">Focus Block</span>
                  </div>
                  <div class="rf-section-label" style="margin-top:8px">Wednesday <span>Oct 28</span></div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-time">12:00 pm – 6:00 pm</span>
                    <span class="rf-item-text">Focus Block</span>
                  </div>
                </template>

                <!-- TRANSLATE -->
                <template v-else-if="activeIndex === 5">
                  <div class="rf-mock-greeting">
                    <div>
                      <p class="rf-mock-hello">Translate</p>
                      <p class="rf-mock-sub">English → <strong>Spanish</strong></p>
                    </div>
                    <div class="rf-lang-chips">
                      <span class="rf-lang-chip rf-lang-chip--active">EN</span>
                      <span class="rf-lang-arrow">→</span>
                      <span class="rf-lang-chip">ES</span>
                    </div>
                  </div>
                  <div class="rf-win-divider"></div>
                  <div class="rf-translate-block">
                    <p class="rf-translate-source">"The quick brown fox jumps over the lazy dog."</p>
                    <div class="rf-win-divider"></div>
                    <p class="rf-translate-result">«El veloz zorro marrón salta sobre el perro perezoso.»</p>
                  </div>
                  <div class="rf-list-item" style="margin-top:4px">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">Copy Translation</span>
                  </div>
                  <div class="rf-list-item">
                    <span class="rf-item-dot"></span>
                    <span class="rf-item-text">Swap Languages</span>
                  </div>
                </template>

              </div><!-- /rf-win-body -->
            </div><!-- /rf-window -->
          </transition>
        </div>

        <!-- Progress bar -->
        <div class="rf-progress-track">
          <div class="rf-progress-fill" :style="{ width: progress + '%' }"></div>
        </div>

        <!-- Feature pill + description -->
        <div class="rf-feat-info">
          <div class="rf-feat-pill">
            <span class="rf-pill-icon">
              <component :is="features[activeIndex].iconComponent" />
            </span>
            <span class="rf-pill-label">{{ features[activeIndex].name }}</span>
          </div>
          <p class="rf-feat-desc">{{ features[activeIndex].desc }}</p>
        </div>

      </div><!-- /rf-right -->
    </div><!-- /rf-grid -->
  </div>
</template>

<script>
import { defineComponent, h } from 'vue';

const IconNote = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('rect', { x: 2, y: 1, width: 10, height: 12, rx: 2, stroke: 'currentColor', 'stroke-width': 1.3 }), h('path', { d: 'M4.5 5h5M4.5 7.5h5M4.5 10h3', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round' })]) });
const IconFlight = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('path', { d: 'M1 10l4-2.5 1 2 4.5-5.5L12 2l-1.5 4.5-4.5 1.5-1-2L1 10z', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linejoin': 'round' })]) });
const IconSearch = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('circle', { cx: 6, cy: 6, r: 4, stroke: 'currentColor', 'stroke-width': 1.3 }), h('path', { d: 'M9.5 9.5l3 3', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round' })]) });
const IconWindow = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('rect', { x: 1, y: 1, width: 5.5, height: 5.5, rx: 1, stroke: 'currentColor', 'stroke-width': 1.3 }), h('rect', { x: 7.5, y: 1, width: 5.5, height: 5.5, rx: 1, stroke: 'currentColor', 'stroke-width': 1.3 }), h('rect', { x: 1, y: 7.5, width: 5.5, height: 5.5, rx: 1, stroke: 'currentColor', 'stroke-width': 1.3 }), h('rect', { x: 7.5, y: 7.5, width: 5.5, height: 5.5, rx: 1, stroke: 'currentColor', 'stroke-width': 1.3 })]) });
const IconCalendar = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('rect', { x: 1, y: 2.5, width: 12, height: 10, rx: 1.5, stroke: 'currentColor', 'stroke-width': 1.3 }), h('path', { d: 'M1 6h12M4.5 1v3M9.5 1v3', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round' })]) });
const IconTranslate = defineComponent({ render: () => h('svg', { width: 14, height: 14, viewBox: '0 0 14 14', fill: 'none' }, [h('path', { d: 'M1 3h6M4 1v2M2 3c0 2 1.5 4 3 4M4.5 7C3.5 5.5 2 4 2 3', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round' }), h('path', { d: 'M8 9l1.5-4 1.5 4M8.6 7.5h2.8', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round' }), h('path', { d: 'M7.5 5h5M7.5 13h5', stroke: 'currentColor', 'stroke-width': 1.3, 'stroke-linecap': 'round', opacity: 0 })]) });

export default {
  name: 'RaycastFeatures',

  components: { IconNote, IconFlight, IconSearch, IconWindow, IconCalendar, IconTranslate },

  data() {
    return {
      activeIndex: 4,
      hoveredIndex: null,
      progress: 0,
      paused: false,
      timer: null,
      features: [
        {
          phrase: 'It can take notes.',
          name: 'Notes',
          iconComponent: 'IconNote',
          searchText: 'Search notes...',
          desc: 'Capture thoughts instantly. Create, search, and organize your notes without ever leaving the keyboard.',
        },
        {
          phrase: 'Track your flights.',
          name: 'Flight Tracker',
          iconComponent: 'IconFlight',
          searchText: 'Enter flight number...',
          desc: 'Stay up to date with live flight status, gate changes, and arrival times — all in one place.',
        },
        {
          phrase: 'Search files.',
          name: 'File Search',
          iconComponent: 'IconSearch',
          searchText: 'Search your Mac...',
          desc: 'Find any file on your Mac in milliseconds. Full-text search across documents, images, and more.',
        },
        {
          phrase: 'Manage your windows.',
          name: 'Window Management',
          iconComponent: 'IconWindow',
          searchText: 'Window Management',
          desc: 'Resize, reorganize and move your focused window without touching your mouse.',
        },
        {
          phrase: 'Plan your day.',
          name: 'Calendar',
          iconComponent: 'IconCalendar',
          searchText: 'Filter by title',
          desc: 'See your schedule at a glance, join meetings, and plan upcoming events without switching apps.',
        },
        {
          phrase: 'Translate into any language.',
          name: 'Translator',
          iconComponent: 'IconTranslate',
          searchText: 'Type to translate...',
          desc: 'Translate text into any language in seconds and copy it straight to your clipboard.',
        },
      ],
    };
  },

  mounted() {
    this.startTimer();
  },

  beforeUnmount() {
    this.stopTimer();
  },

  methods: {
    startTimer() {
      this.timer = setInterval(() => {
        if (!this.paused) {
          this.progress += 1;
          if (this.progress >= 100) {
            this.progress = 0;
            this.activeIndex = (this.activeIndex + 1) % this.features.length;
          }
        }
      }, 60);
    },
    stopTimer() {
      if (this.timer) clearInterval(this.timer);
    },
    pauseTimer() {
      this.paused = true;
    },
    resumeTimer() {
      this.paused = false;
      this.hoveredIndex = null;
    },
    jumpTo(i) {
      this.activeIndex = i;
      this.progress = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.rf-root {
  background: #111110;
  border-radius: 0;
  padding: 64px 56px;
  font-family: 'Geist', 'Inter', ui-sans-serif, sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 540px;
  display: flex;
  align-items: center;
}

/* ── GRID ── */
.rf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

/* ── LEFT COLUMN ── */
.rf-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rf-heading {
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.rf-text {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.55;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.25);
  cursor: default;
}

.rf-phrase {
  cursor: pointer;
  transition: color 0.18s ease;
  display: inline;

  &--lit {
    color: #ffffff;
  }

  &--static {
    cursor: default;
    color: rgba(255, 255, 255, 0.25);
  }
}

/* ── RIGHT COLUMN ── */
.rf-right {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Mockup area */
.rf-mockup-wrap {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #1c1c1e;
}

.rf-mockup-bg {
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(
      -45deg,
      rgba(255,255,255,0.018) 0px,
      rgba(255,255,255,0.018) 1px,
      transparent 1px,
      transparent 18px
    );
  pointer-events: none;
  z-index: 0;
}

/* Raycast window */
.rf-window {
  position: relative;
  z-index: 1;
  margin: 20px;
  background: rgba(30, 30, 32, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.rf-win-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
}

.rf-win-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.07);
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  flex-shrink: 0;
}

.rf-win-search {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  flex: 1;
}

.rf-win-cursor {
  display: inline-block;
  width: 1.5px;
  height: 14px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 1px;
  animation: rf-blink 1s step-end infinite;
  margin-right: 2px;
}

@keyframes rf-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.rf-win-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
  margin: 0;
}

.rf-win-body {
  padding: 10px 0 14px;
  min-height: 180px;
}

/* Greeting row */
.rf-mock-greeting {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 14px 10px;
  gap: 12px;
}

.rf-mock-hello {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 3px;
}

.rf-mock-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);

  strong {
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
  }
}

.rf-next-up {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  text-align: right;
  line-height: 1.4;
  flex-shrink: 0;

  strong {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
  }
}

/* Section label */
.rf-section-label {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  padding: 6px 14px 4px;
  letter-spacing: 0.01em;

  span {
    font-weight: 400;
    color: rgba(255, 255, 255, 0.3);
    margin-left: 6px;
  }
}

/* List items */
.rf-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  transition: background 0.1s;

  &--active {
    background: rgba(255, 255, 255, 0.06);

    .rf-item-text {
      color: #ffffff;
    }
  }
}

.rf-item-dot {
  width: 14px;
  height: 14px;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  flex-shrink: 0;

  &--yellow {
    border-color: #f5a623;
    background: rgba(245, 166, 35, 0.1);
  }
}

.rf-item-time {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
  min-width: 100px;
}

.rf-item-text {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.65);
}

/* Badge */
.rf-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 20px;
  flex-shrink: 0;

  &--green {
    background: rgba(52, 199, 89, 0.15);
    color: #34c759;
    border: 1px solid rgba(52, 199, 89, 0.25);
  }
}

/* Flight card */
.rf-flight-card {
  margin: 4px 14px 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.03);

  &--active {
    border-color: rgba(245, 166, 35, 0.3);
    background: rgba(245, 166, 35, 0.05);
  }
}

.rf-flight-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.rf-flight-city {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &--right {
    text-align: right;
    align-items: flex-end;
  }
}

.rf-flight-code {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.03em;
}

.rf-flight-time {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
}

.rf-flight-line {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.rf-flight-duration {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
}

.rf-flight-track {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  position: relative;
}

.rf-flight-plane {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.6);
}

.rf-flight-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.35);
}

/* File icons */
.rf-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;

  &--fig { background: rgba(162, 89, 255, 0.2); color: #a259ff; }
  &--pdf { background: rgba(255, 59, 48, 0.2); color: #ff3b30; }
  &--png { background: rgba(52, 199, 89, 0.2); color: #34c759; }
}

.rf-file-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rf-file-path {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-family: 'Geist Mono', monospace;
}

/* Window management grid */
.rf-win-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding: 8px 14px;
}

.rf-win-cell {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.02);
  transition: all 0.15s;

  &--active {
    border-color: rgba(245, 166, 35, 0.4);
    background: rgba(245, 166, 35, 0.08);

    .rf-win-app { color: #f5a623; }
  }
}

.rf-win-app {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 500;
  text-align: center;
}

.rf-win-commands {
  display: flex;
  gap: 6px;
  padding: 4px 14px 0;
}

.rf-cmd-chip {
  font-size: 9.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  padding: 3px 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.03);

  &--active {
    color: #f5a623;
    border-color: rgba(245, 166, 35, 0.35);
    background: rgba(245, 166, 35, 0.08);
  }
}

/* Language chips */
.rf-lang-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.rf-lang-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.04);

  &--active {
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.08);
  }
}

.rf-lang-arrow {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
}

/* Translation blocks */
.rf-translate-block {
  margin: 2px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  overflow: hidden;
}

.rf-translate-source {
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  font-style: italic;
  line-height: 1.5;
}

.rf-translate-result {
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-style: italic;
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.03);
}

/* ── PROGRESS BAR ── */
.rf-progress-track {
  height: 2px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0 0 14px 14px;
  overflow: hidden;
}

.rf-progress-fill {
  height: 100%;
  background: #ff6b6b;
  border-radius: 0;
  transition: width 0.06s linear;
}

/* ── FEATURE INFO ── */
.rf-feat-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 16px;
}

.rf-feat-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 4px 12px 4px 8px;
  width: fit-content;
}

.rf-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(87, 142, 247, 0.25);
  border-radius: 6px;
  color: #578ef7;
}

.rf-pill-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.rf-feat-desc {
  font-size: 12.5px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.35);
  max-width: 320px;
}

/* ── TRANSITION ── */
.rf-fade-enter-active,
.rf-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.rf-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.rf-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── RESPONSIVE ── */
@media (max-width: 700px) {
  .rf-root { padding: 40px 24px; }
  .rf-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .rf-text { font-size: 17px; }
}
</style>
